// Adaptador concreto del repositorio de lead (capa HTTP).
// viceversa para la respuesta — el dominio nunca ve el wire format.
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { LeadRepository } from '../../../domain/repositories/lead/lead.repository';
import {
  CreateLeadAdditionalInfo,
  CreateLeadBusinessError,
  CreateLeadInput,
  CreateLeadResult,
  LeadVehicleInfo,
  ReportLeadExceptionInput,
  UpdateLeadInput,
  UpdateLeadPauseInput,
  UpdateLeadPrendaInput,
  UpdateLeadResult,
} from '../../../domain/interfaces/lead/lead.interface';
import { HttpClientService } from '../../services/http-client/http-client.service';
import { AuthService } from '../../services/auth/auth.service';
import { API_ENDPOINTS } from '../../config/api.config';
import { ApiResponse } from '../../../domain/interfaces/api-response.model';

interface CreateLeadRequestDto {
  plate: string;
  confirm_plate: string;
  circulation_zone_id: string;
  insured_value_0Km: string;
  use_type: string;
  assessor_code: string | null;
  set_utm_source: string;
  set_utm_medium: string;
  set_utm_campaign: string;
  set_utm_content: string;
  set_utm_term: string;
  accept_tos: boolean;
}

interface LeadResponseVehicleOwnerDocumentDto {
  number?: string;
  type?: string;
}

interface LeadResponseVehicleOwnerDto {
  firstnames?: string;
  lastnames?: string;
  document?: LeadResponseVehicleOwnerDocumentDto;
}

interface LeadResponseVehicleDto {
  brand?: string;
  line?: string;
  model?: string;
  plate?: string;
  circulation_zone_id?: string | number;
  use_type?: string;
  Prenda?: string;
  // No observados aún en una respuesta real, mantenidos por si el gateway los envía
  // en otros escenarios (vehículo con accesorios / valor asegurado RUNT).
  TieneAccesoriosAdicionales?: boolean;
  ValorAseguradoRunt?: number;
  owner?: LeadResponseVehicleOwnerDto;
}

interface LeadResponseDataDto {
  Id?: string;
  vehicle?: LeadResponseVehicleDto;
}

interface LeadResponseAdditionalInfoDto {
  '0KM'?: boolean;
  ExceptionalDeductible?: string;
  isPrenda?: string;
  prendario?: string;
}

/**
 * Envelope REAL observado en QA: `{ additionalInfo, data }`, con `additionalInfo` como
 * HERMANO de `data` (no anidado dentro). No sigue el contrato genérico IResult<T>
 * (success/message/statusCode) del resto del gateway — por eso se usa `postRaw`.
 */
interface LeadApiEnvelope {
  data?: LeadResponseDataDto;
  additionalInfo?: LeadResponseAdditionalInfoDto;
  message?: string;
}

/**
 * Body real de error de negocio, confirmado contra 16 escenarios reales de capa media (RUNT,
 * Fasecolda, prenda, blindado, antihurto, salvamento, antigüedad, etc.).
 *
 * IMPORTANTE: el gateway responde SIEMPRE con HTTP 200 — nunca con status 422 real — así que
 * este shape nunca llega por `catchError`/`HttpErrorResponse`. El único discriminador válido
 * es el contenido del body (`errorCode === 'Entity_Not_Processable'`), confirmado dentro del
 * `map()` de la respuesta exitosa. Notar `lead_id` (snake_case, minúscula) — distinto de
 * `data.Id` que trae el éxito real.
 */
interface CreateLeadErrorResponseDto {
  data?: { lead_id?: string };
  error?: number;
  errorCode?: string;
  message?: string;
}

// ---- updateLead (PUT /clientes/autos/updateLead/{leadId}) ----
// A diferencia de crearLead, INTEGRACIONES-SPEC.md no documenta una excepción de envelope para
// este endpoint: se asume el contrato genérico IResult<T> (ApiResponse<T>) hasta verificar contra
// una respuesta real de QA.

interface UpdateLeadPayerRequestDto {
  first_name: string;
  last_name: string;
  birth_date: string;
  document_type: string;
  document_number: string;
  document_issue_date: string;
  birth_country: string;
  address: string;
  city_department: string;
  phone: string;
  email: string;
}

/**
 * DTO inferido por analogía a CreateLeadRequestDto — nombres de campo NO verificados contra
 * una respuesta real (a diferencia de prenda/pausa, cuyo body viene documentado literal en
 * INTEGRACIONES-SPEC.md §5.1).
 */
interface UpdateLeadRequestDto {
  birth_country: string;
  birth_date: string;
  document_issue_date: string;
  address: string;
  city_department: string;
  phone: string;
  email: string;
  accept_tos: boolean;
  payer?: UpdateLeadPayerRequestDto;
}

/** Body exacto documentado en el spec. */
interface UpdateLeadPrendaRequestDto {
  prendario: string;
  accept_tos: true;
}

/** Body exacto documentado en el spec. */
interface UpdateLeadPauseRequestDto {
  firstname?: string;
  lastname?: string;
  phone: string;
  email: string;
  accept_tos: boolean;
  timestamp: string;
  assessor_code?: string;
}

interface UpdateLeadResponseDataDto {
  Id?: string;
  id?: string;
}


interface ReportLeadExceptionRequestDto {
  required_status: string;
  percentege_lead: number;
  estadoRescate?: string;
  estadoRetoma?: string;
  Leadstatus?: string;
  product: string;
  session_id: string;
}

const REPORT_LEAD_EXCEPTION_PRODUCT = 'Autos Digital';

/**
 * Implementación del repositorio de lead.
 * Llama POST /clientes/autos/lead (dispara RUNT + Fasecolda del lado del gateway).
 */
@Injectable()
export class LeadRepositoryImplementation extends LeadRepository {
  private readonly http = inject(HttpClientService);
  private readonly authService = inject(AuthService);

  override createLead(input: CreateLeadInput): Observable<CreateLeadResult> {
    return this.http
      .postRaw<LeadApiEnvelope | CreateLeadErrorResponseDto>(
        API_ENDPOINTS.leads.create,
        this.toRequestDto(input),
      )
      .pipe(map((response) => this.toResultOrThrowBusinessError(response)));
  }

  /**
   * El gateway responde 200 tanto en éxito como en error de negocio — el shape del body es lo
   * único que cambia. Si trae `errorCode: 'Entity_Not_Processable'` + `data.lead_id`, se lanza
   * como `CreateLeadBusinessError` tipado (RxJS convierte el `throw` de un `map` en error del
   * observable). Cualquier otro shape se procesa como éxito normal.
   */
  private toResultOrThrowBusinessError(
    response: LeadApiEnvelope | CreateLeadErrorResponseDto,
  ): CreateLeadResult {
    if (this.isBusinessErrorBody(response)) {
      const businessError: CreateLeadBusinessError = {
        leadId: response.data?.lead_id ?? '',
        errorCode: response.errorCode ?? '',
        message: response.message ?? '',
      };
      throw businessError;
    }
    return this.toResult(response);
  }

  private isBusinessErrorBody(
    response: LeadApiEnvelope | CreateLeadErrorResponseDto,
  ): response is CreateLeadErrorResponseDto {
    const body = response as CreateLeadErrorResponseDto;
    if (body.errorCode === 'Entity_Not_Processable' && !!body.data?.lead_id) return true;
    const hasLead = !!(response as LeadApiEnvelope).data?.Id;
    return !!body.errorCode && !hasLead;
  }

  private toRequestDto(input: CreateLeadInput): CreateLeadRequestDto {
    return {
      plate: input.plate,
      confirm_plate: input.confirmPlate,
      circulation_zone_id: input.circulationZoneId,
      insured_value_0Km: input.insuredValue0Km,
      use_type: input.useType,
      assessor_code: input.assessorCode,
      set_utm_source: input.utmSource,
      set_utm_medium: input.utmMedium,
      set_utm_campaign: input.utmCampaign,
      set_utm_content: input.utmContent,
      set_utm_term: input.utmTerm,
      accept_tos: input.acceptTos,
    };
  }

  private toResult(response: LeadApiEnvelope): CreateLeadResult {
    const data = response.data ?? {};
    return {
      id: data.Id ?? '',
      vehicle: data.vehicle ? this.toVehicleInfo(data.vehicle) : null,
      additionalInfo: this.toAdditionalInfo(response.additionalInfo),
      message: response.message,
    };
  }

  private toVehicleInfo(vehicle: LeadResponseVehicleDto): LeadVehicleInfo {
    return {
      brand: vehicle.brand,
      line: vehicle.line,
      model: vehicle.model,
      plate: vehicle.plate,
      circulationZoneId:
        vehicle.circulation_zone_id !== undefined
          ? String(vehicle.circulation_zone_id)
          : undefined,
      useType: vehicle.use_type,
      pledge: vehicle.Prenda,
      hasAdditionalAccessories: vehicle.TieneAccesoriosAdicionales,
      insuredValueRunt: vehicle.ValorAseguradoRunt,
      owner: vehicle.owner
        ? {
            firstNames: vehicle.owner.firstnames,
            lastNames: vehicle.owner.lastnames,
            document: vehicle.owner.document
              ? { number: vehicle.owner.document.number, type: vehicle.owner.document.type }
              : undefined,
          }
        : undefined,
    };
  }

  private toAdditionalInfo(
    additionalInfo: LeadResponseAdditionalInfoDto | undefined,
  ): CreateLeadAdditionalInfo | undefined {
    if (!additionalInfo) return undefined;
    return {
      newVehicle: additionalInfo['0KM'],
      exceptionalDeductible: additionalInfo.ExceptionalDeductible,
      isPledge: additionalInfo.isPrenda,
      pledgeEntity: additionalInfo.prendario,
    };
  }

  override updateLead(input: UpdateLeadInput): Observable<UpdateLeadResult> {
    const dto: UpdateLeadRequestDto = {
      birth_country: input.owner.birthCountry,
      birth_date: input.owner.birthDate,
      document_issue_date: input.owner.documentIssueDate,
      address: input.owner.address,
      city_department: input.owner.cityDepartment,
      phone: input.owner.phone,
      email: input.owner.email,
      accept_tos: input.owner.acceptTos,
      payer: input.payer ? this.toPayerRequestDto(input.payer) : undefined,
    };
    return this.http
      .put<UpdateLeadResponseDataDto>(API_ENDPOINTS.leads.update(input.leadId), dto)
      .pipe(map((response) => this.toUpdateResult(response)));
  }

  override updateLeadPrenda(input: UpdateLeadPrendaInput): Observable<UpdateLeadResult> {
    const dto: UpdateLeadPrendaRequestDto = {
      prendario: input.prendario,
      accept_tos: true,
    };
    return this.http
      .put<UpdateLeadResponseDataDto>(API_ENDPOINTS.leads.update(input.leadId), dto)
      .pipe(map((response) => this.toUpdateResult(response)));
  }

  override saveLeadPauseData(input: UpdateLeadPauseInput): Observable<UpdateLeadResult> {
    const dto: UpdateLeadPauseRequestDto = {
      firstname: input.firstName,
      lastname: input.lastName,
      phone: input.phone,
      email: input.email,
      accept_tos: input.acceptTos,
      timestamp: input.timestamp,
      assessor_code: input.assessorCode,
    };
    return this.http
      .put<UpdateLeadResponseDataDto>(API_ENDPOINTS.leads.update(input.leadId), dto)
      .pipe(map((response) => this.toUpdateResult(response)));
  }

  private toPayerRequestDto(
    payer: NonNullable<UpdateLeadInput['payer']>,
  ): UpdateLeadPayerRequestDto {
    return {
      first_name: payer.firstName,
      last_name: payer.lastName,
      birth_date: payer.birthDate,
      document_type: payer.documentType,
      document_number: payer.documentNumber,
      document_issue_date: payer.documentIssueDate,
      birth_country: payer.birthCountry,
      address: payer.address,
      city_department: payer.cityDepartment,
      phone: payer.phone,
      email: payer.email,
    };
  }

  private toUpdateResult(response: ApiResponse<UpdateLeadResponseDataDto>): UpdateLeadResult {
    const data = response.data ?? {};
    return {
      id: data.Id ?? data.id ?? '',
      message: response.message,
    };
  }

  override reportLeadException(input: ReportLeadExceptionInput): Observable<void> {
    const dto: ReportLeadExceptionRequestDto = {
      required_status: input.requiredStatus,
      percentege_lead: input.percentageLead,
      estadoRescate: input.estadoRescate,
      estadoRetoma: input.estadoRetoma,
      Leadstatus: input.leadStatus,
      product: REPORT_LEAD_EXCEPTION_PRODUCT,
      session_id: this.authService.getSessionId(),
    };
    return this.http
      .postRaw<unknown>(API_ENDPOINTS.leads.exception(input.leadId), dto)
      .pipe(map(() => undefined));
  }
}
